import React, { useContext } from 'react';
import api from '../../../Api';
import { groupBy } from '../../../util';
import PropTypes from 'prop-types';
import { useCommunities } from '../../Communities/containers/communitiesDataStore';
import {
	getFilterRefinementDefinitionToSave,
	addSavedFiltersToState,
} from '../util';
import { useUser } from '../../User/containers/UserDataStore';

const SavedCommunityFiltersContext = React.createContext();

SavedCommunityFiltersDataStore.propTypes = {
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]),
	communityId: PropTypes.number,
	isOnDrawer: PropTypes.bool,
};

export default function SavedCommunityFiltersDataStore({
	children,
	isOnDrawer,
	communityId,
}) {
	const [savedCommunityFilters, setSavedCommunityFilters] = React.useState(
		[],
	);
	const [community, setCommunity] = React.useState({});
	const [isLoading, setIsLoading] = React.useState(true);
	const doesHaveEdits = React.useRef();
	const isMounted = React.useRef();
	const request = React.useRef();
	const communitiesDataStore = useCommunities();
	const { isAnonymous } = useUser();

	React.useEffect(() => {
		fetchSavedCommunityFilters();
		isMounted.current = true;

		return () => {
			api.revoke(request.current);
			isMounted.current = false;
			doesHaveEdits.current &&
				isOnDrawer &&
				communitiesDataStore.refreshWithLoadingState();
		};
	}, []);

	const saveSavedCommunityFilters = async filters => {
		const grouped = groupBy(filters, 'viewId');
		const communityDetails = await fetchCommunityDetails();
		if (communityDetails) {
			setCommunity(communityDetails);
			const communityViewId = communityDetails.viewId;
			communityViewId
				? setSavedCommunityFilters(
						grouped[communityViewId.toString()] || [],
				  )
				: setSavedCommunityFilters([]);
		}
		setIsLoading(false);
	};

	const fetchCommunityDetails = async () => {
		try {
			const communityRequest = api.get(
				`/api/v1/affiliations/${communityId}/summary`,
			);
			const { affiliations: community } = await communityRequest;
			return community[0];
		} catch (e) {
			console.error(e);
		}
	};

	const fetchSavedCommunityFilters = async () => {
		if (!isAnonymous()) {
			try {
				request.current = api.cancelableGet('/api/v1/affiliations/me');
				const data = await request.current.promise;
				const { browseRefinements } = data;
				isMounted.current &&
					saveSavedCommunityFilters(browseRefinements);
			} catch (e) {
				console.error(e);
				isMounted.current && setIsLoading(false);
			}
		} else {
			setIsLoading(false);
		}
	};

	const refreshFilters = () => {
		isMounted.current && setIsLoading(true);
		fetchSavedCommunityFilters();
	};

	const saveFilters = (activeCategories, browseLaneType) => {
		doesHaveEdits.current = true;
		var thisSavedFilter = savedCommunityFilters.filter(
			sf => sf.viewId == community.viewId,
		);
		thisSavedFilter = thisSavedFilter.filter(
			tsf => tsf.browseLaneType !== browseLaneType.key,
		);
		addSavedFiltersToState(
			thisSavedFilter,
			activeCategories,
			browseLaneType,
			community.viewId,
		);
		processBrowseRefinements(thisSavedFilter);
	};

	const processBrowseRefinements = thisSavedFilter => {
		const definition = getFilterRefinementDefinitionToSave(thisSavedFilter);
		const groupBrowseViewDTO = {
			viewId: community.viewId,
			definition: JSON.stringify(definition),
			groupId: communityId,
		};
		if (!community.viewId) {
			api.post(
				`/api/v1/affiliations/${communityId}/filters`,
				groupBrowseViewDTO,
			).then(() => refreshFilters());
		} else {
			api.put(
				`/api/v1/affiliations/${communityId}/filters`,
				groupBrowseViewDTO,
			).then(() => refreshFilters());
		}
	};

	const getStore = () => ({
		savedCommunityFilters,
		isLoading,
		community,
		saveFilters: (activeCategories, browseLaneType) =>
			saveFilters(activeCategories, browseLaneType),
		processBrowseRefinements: (
			communityId,
			viewId,
			definition,
			filterType,
		) =>
			processBrowseRefinements(
				communityId,
				viewId,
				definition,
				filterType,
			),
	});

	const savedCommunityFiltersData = getStore();

	return (
		<SavedCommunityFiltersContext.Provider
			value={savedCommunityFiltersData}>
			{typeof children === 'function'
				? children(savedCommunityFiltersData)
				: children}
		</SavedCommunityFiltersContext.Provider>
	);
}

export const useSavedCommunityFilters = () =>
	useContext(SavedCommunityFiltersContext);
